import {
  formatCountdown,
  formatDate,
  formatDay,
  formatTime,
  statusLabel,
  statusTone,
  truncate,
  type Tone,
} from "./formatters";
import { isTranslatedMetaError, translateMetaError } from "./meta-errors";
import { allFlowButtons } from "./flow-model";
import { CAPTION_LIMIT_CARD } from "./media-views";
import type { CommentEvent, ConversationFlow, MediaItem, Rule, RuleFunnel, RuleTotals } from "../api/types";

/**
 * The automations: their card, their figures and the trail they leave.
 *
 * The metrics live here and not apart because they do not read on their own: a ratio only
 * says something next to the rule that produced it.
 */

/**
 * What sets an automation in motion, under the names the editor reads.
 *
 * They are ticked, not chosen: an automation can wait for its keyword under a post and in
 * the inbox at once, which is where a good part of the people who saw the post write it.
 */
export type TriggerId = "comment" | "direct_message";

export const TRIGGER_OPTIONS: { id: TriggerId; label: string }[] = [
  { id: "comment", label: "Cuando comentan una publicación" },
  { id: "direct_message", label: "Cuando me escriben por mensaje directo" },
];

export const TRIGGER_IDS = TRIGGER_OPTIONS.map((option) => option.id);

/** How the card says it in one line, without room for two sentences. */
const TRIGGER_LABELS: Record<TriggerId | "both", string> = {
  comment: "COMENTARIO",
  direct_message: "MENSAJE DIRECTO",
  both: "COMENTARIO Y MENSAJE",
};

/** The triggers of a stored rule, including the ones saved as a single `trigger`. */
export function ruleTriggers(rule: Pick<Rule, "triggers"> & { trigger?: string }): TriggerId[] {
  const triggers: string[] = rule.triggers ?? [rule.trigger || "comment"];
  return TRIGGER_IDS.filter((id) => triggers.includes(id));
}

/**
 * How long to wait before insisting.
 *
 * No option reaches 24 hours: past that deadline Instagram no longer allows writing, and
 * offering it would promise a reminder the API would reject.
 */
export const FOLLOW_UP_OPTIONS = [
  { id: "0", label: "No insistir" },
  { id: "60", label: "1 hora después" },
  { id: "180", label: "3 horas después" },
  { id: "360", label: "6 horas después" },
  { id: "720", label: "12 horas después" },
  { id: "1200", label: "20 horas después" },
];

/**
 * How long to wait before answering their comment in public.
 *
 * Here the options do pass 24 hours, unlike the reminder's: answering a comment is not a
 * message and Instagram puts no deadline on it. That is the whole point — these are the
 * people we can no longer write to privately.
 */
export const NUDGE_OPTIONS = [
  { id: "0", label: "No comentar" },
  { id: "120", label: "2 horas después" },
  { id: "240", label: "4 horas después" },
  { id: "480", label: "8 horas después" },
  { id: "1440", label: "1 día después" },
  { id: "2880", label: "2 días después" },
];

/**
 * The follow gate, as the editor reads it.
 *
 * The two button labels are fixed and not editable: they are the same promise in every
 * automation, and leaving them open only invites writing one Instagram truncates at twenty
 * characters. They are shown so whoever configures sees exactly what the person will get.
 */
export const FOLLOW_GATE_BUTTON_LABELS = ["Visitar perfil", "Ya soy seguidor"];

/** The same default texts as the domain, so the editor shows what will actually be sent. */
export const DEFAULT_FOLLOW_GATE_TEXT =
  "¡Casi! Para enviártelo necesito que me sigas 💛 Sígueme y toca «Ya soy seguidor».";

export const DEFAULT_FOLLOW_GATE_RETRY_TEXT =
  "Acabo de mirarlo y todavía no me sigues 👀 Sígueme y vuelve a tocar «Ya soy seguidor». Si lo has hecho hace un segundo, dale un momento y prueba otra vez.";

const MATCH_DESCRIPTIONS: Record<string, string> = {
  any: "cualquier palabra activa la regla",
  all: "el comentario contiene todas las palabras",
};

/** The same cap as the domain: going over here only produces an error on save. */
export const MAX_REPLY_VARIANTS = 5;

/** Sums the flow up in one line: how many options it offers and how far it can go. */
function flowSummary(flow: ConversationFlow | null | undefined): string {
  if (!flow) return "";
  // A rule stored before some of the flow's parts existed may lack them.
  const partial: Partial<ConversationFlow> = flow;
  const buttons = allFlowButtons({
    buttons: partial.buttons ?? [],
    messages: partial.messages ?? [],
    steps: partial.steps ?? [],
  }).length;
  if (buttons === 0) return "";
  const steps = flow.steps?.length ?? 0;
  const options = `${buttons} ${buttons === 1 ? "opción" : "opciones"}`;
  return steps === 0 ? options : `${options} · ${steps} ${steps === 1 ? "paso" : "pasos"}`;
}

/** A ratio between 0 and 1 as a whole percentage. `null` reads as a dash. */
export function formatRate(rate: number | null | undefined): string {
  return rate === null || rate === undefined ? "—" : `${Math.round(rate * 100)}%`;
}

/**
 * A rule's figures, ready for the card.
 *
 * They are only shown if the rule has done something: a row of zeros under every freshly
 * created automation is noise that pushes what matters further down.
 */
function toRuleMetricsView(totals: Partial<RuleTotals> | null | undefined) {
  const comments = totals?.comments ?? 0;
  const conversations = totals?.conversations ?? 0;
  const replies = totals?.replies ?? 0;

  return {
    active: comments > 0 || conversations > 0,
    comments: String(comments),
    sent: String(totals?.sent ?? 0),
    conversations: String(conversations),
    replies: String(replies),
    failed: String(totals?.failed ?? 0),
    hasFailures: (totals?.failed ?? 0) > 0,
    // How many of those served sent any signal back: the figure that says whether the flow
    // catches on.
    replyRate: formatRate(conversations > 0 ? Math.min(1, replies / conversations) : null),
  };
}

export interface RuleHandlers {
  onToggle?: (rule: Rule) => void;
  onEdit?: (rule: Rule) => void;
  onDelete?: (rule: Rule) => void;
  onSimulate?: (rule: Rule) => void;
  onPreview?: (rule: Rule) => void;
  onFunnel?: (rule: Rule) => void;
}

export function toRuleView(
  rule: Rule,
  mediaById: Map<string, MediaItem>,
  handlers: RuleHandlers = {},
  totals: Partial<RuleTotals> | null = null,
  openFunnelId: number | null = null,
) {
  const media = mediaById.get(rule.mediaId);
  const flowLabel = flowSummary(rule.flow);
  const metrics = toRuleMetricsView(totals);
  const triggers = ruleTriggers(rule);
  return {
    ...rule,
    metrics,
    isFunnelOpen: openFunnelId === rule.id,
    funnelAriaExpanded: openFunnelId === rule.id ? "true" : "false",
    // With nobody served the funnel is three rungs at zero: it is not offered yet.
    canSeeFunnel: metrics.active,
    funnelLabel: openFunnelId === rule.id ? "Ocultar embudo" : "Ver embudo",
    onFunnel: () => handlers.onFunnel?.(rule),
    index: String(rule.id).padStart(2, "0"),
    statusLabel: rule.enabled ? "ACTIVA" : "PAUSADA",
    statusTone: (rule.enabled ? "success" : "neutral") as Tone,
    matchDesc: MATCH_DESCRIPTIONS[rule.matchMode] ?? MATCH_DESCRIPTIONS.any,
    isDirectMessage: !triggers.includes("comment"),
    triggerLabel: triggers.length > 1 ? TRIGGER_LABELS.both : TRIGGER_LABELS[triggers[0] ?? "comment"],
    keyword: rule.keywords.join(", "),
    reply: rule.publicReply || "—",
    dm: rule.privateReply,
    flowLabel,
    hasFlow: Boolean(flowLabel),
    // A rule that delivers nothing to non-followers behaves very differently from the
    // rest: that has to show in the list and not only when the editor is opened.
    requiresFollow: Boolean(rule.requireFollow),
    // An inbox rule hangs off no post: naming an id would be a lie.
    post: !triggers.includes("comment")
      ? "Mensajes al buzón"
      : media
        ? truncate(media.caption, CAPTION_LIMIT_CARD)
        : // A post no longer among the synced ones: its bare id meant nothing to anybody.
          `Publicación sin sincronizar · ${String(rule.mediaId).slice(-8)}`,
    toggleLabel: rule.enabled ? "Pausar" : "Activar",
    onToggle: () => handlers.onToggle?.(rule),
    onEdit: () => handlers.onEdit?.(rule),
    onDelete: () => handlers.onDelete?.(rule),
    onSimulate: () => handlers.onSimulate?.(rule),
    onPreview: () => handlers.onPreview?.(rule),
  };
}

/** States a log row can be retried from by hand. */
const RETRYABLE_STATUSES = new Set(["failed", "discarded"]);

/** States in which the comment is waiting its turn: the queue will come back to it. */
const QUEUED_STATUSES = new Set(["pending", "scheduled", "retry"]);

/**
 * How much of a comment fits in a row.
 *
 * The log is a list to scan, not to read: a four-hundred-character comment pushed the next
 * four rows off the screen. The whole text is still there, in the cell's `title`.
 */
const COMMENT_LIMIT_ROW = 90;

/** How the commenter is named when Instagram did not give their username. */
function personLabel(username: unknown, commenterId: unknown): string {
  const handle = String(username ?? "")
    .trim()
    .replace(/^@+/, "");
  if (handle) return `@${handle}`;
  const id = String(commenterId ?? "").trim();
  return id ? `Instagram · ${id.slice(-6)}` : "Persona de Instagram";
}

/**
 * With no handlers no button is offered: that is what tells the log, where one acts, from
 * the summary, which only looks. They are grouped because there are three of them and a
 * positional list does not say which is which at the call site.
 */
export interface ActivityActions {
  onRetry?: ((id: number) => void) | null;
  onDelete?: ((id: number) => void) | null;
  onLaunch?: ((id: number) => void) | null;
  now?: Date;
}

export function toActivityView(
  event: CommentEvent,
  mediaById: Map<string, MediaItem> = new Map(),
  actions: ActivityActions = {},
) {
  const { onRetry = null, onDelete = null, onLaunch = null, now = new Date() } = actions;
  const queued = QUEUED_STATUSES.has(event.status);
  const retryable = RETRYABLE_STATUSES.has(event.status);
  const media = mediaById.get(event.mediaId);
  const comment = String(event.text ?? "");
  const requeues = Number(event.requeues ?? 0);
  // Meta answers in English and in its own vocabulary. In red under a row, that text only
  // says something broke; translated, it also says whether retrying will help.
  const rawError = String(event.lastError ?? "");

  return {
    id: event.id,
    user: personLabel(event.username, event.commenterId),
    comment: truncate(comment, COMMENT_LIMIT_ROW),
    commentFull: comment,
    postId: event.mediaId,
    post: media ? truncate(media.caption, CAPTION_LIMIT_CARD) : `Publicación · ${String(event.mediaId).slice(-8)}`,
    status: statusLabel(event.status),
    tone: statusTone(event.status),
    // The time alone in the column and the full day on hover: the log is almost always
    // looked at to find out what happened today.
    time: formatTime(event.receivedAt),
    day: formatDay(event.receivedAt),
    timeFull: formatDate(event.receivedAt),
    error: translateMetaError(rawError),
    // The original is only offered when there was a translation: it is what serves to
    // search Meta's documentation, but repeating it untranslated would say the same thing
    // twice.
    errorDetail: isTranslatedMetaError(rawError) ? rawError : "",
    hasError: Boolean(rawError),
    // The row-by-row retry also reaches what Instagram said it could not deliver: whoever
    // looks at that row may know what the system cannot — that the person changed their
    // settings. The summary does not offer it, only the log does.
    canRetry: Boolean(onRetry) && retryable,
    // How many times it has already been asked for. It does not hide the button — that is
    // the decision of whoever is looking at the row — but without the figure one presses
    // blind, and insisting six times on the same comment is exactly what nobody wants to
    // find in their sent folder.
    retriedLabel: requeues > 0 ? `${requeues} ${requeues === 1 ? "reintento" : "reintentos"}` : "",
    hasRetried: requeues > 0,
    onRetry: () => onRetry?.(event.id),
    // What is waiting its turn is not "retried": it was going to be tried anyway. All that
    // is offered is moving the clock, which is why it spends no attempts and no presses.
    isQueued: queued,
    countdownLabel: queued ? formatCountdown(event.nextAttemptAt, now) : "",
    canLaunch: Boolean(onLaunch) && queued,
    onLaunch: () => onLaunch?.(event.id),
    // Deleting accompanies the retry rather than replacing it: it is the way out for what
    // fails over and over, which otherwise stays in the group eating a press every pass.
    canDelete: Boolean(onDelete) && retryable,
    onDelete: () => onDelete?.(event.id),
    // A row with nothing to do draws no action strip: with two thousand delivered
    // comments, that gap is half the height of the list.
    hasActions: (Boolean(onRetry) && retryable) || (Boolean(onLaunch) && queued),
  };
}

/**
 * The funnel, ready to paint.
 *
 * The bar is drawn with a percentage width and not with a chart: what has to be seen is
 * where it narrows, and for that one row per rung with its width is enough and reads just
 * as well on a phone.
 */
export function toFunnelView(funnel: RuleFunnel | null | undefined) {
  const steps = funnel?.steps ?? [];
  return {
    hasData: steps.some((step) => step.reached > 0),
    steps: steps.map((step) => ({
      stepId: step.stepId,
      title: step.title,
      preview: step.preview || "—",
      reached: String(step.reached),
      answered: String(step.answered),
      droppedOff: String(step.droppedOff),
      // A visible minimum: a 0% bar is indistinguishable from a broken row.
      barWidth: Math.max(2, Math.round((step.widthRate ?? 0) * 100)),
      rateLabel: formatRate(step.conversionRate),
      // The last rung has nowhere to go: talking about drop-off there only confuses.
      showsDropOff: step.droppedOff > 0,
    })),
  };
}

/**
 * The public reply's variants, as they are edited.
 *
 * The main text does not appear here: it is edited in its own field and heads the set.
 * These are the alternatives, each with its own way of being removed.
 */
export function toVariantsView(
  variants: string[] | null | undefined,
  handlers: { onVariantChange: (index: number, value: string) => void; onVariantRemove: (index: number) => void },
) {
  return (variants ?? []).map((text, index) => ({
    key: String(index),
    text,
    ariaLabel: `Variante ${index + 1} de la respuesta pública`,
    removeLabel: `Quitar la variante ${index + 1}`,
    onText: (value: string) => handlers.onVariantChange(index, value),
    onRemove: () => handlers.onVariantRemove(index),
  }));
}
